import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { ClientLogo } from "@/components/Layout";
import { ArrowLeft, Pencil } from "lucide-react";
import { toast } from "sonner";
import DatabasesPanel from "@/components/DatabasesPanel";
import { fmtDate } from "@/lib/dates";
import LogoUploader from "@/components/client/LogoUploader";
import EditClientDialog from "@/components/client/EditClientDialog";
import ClientProjectsTab from "@/components/client/ProjectsTab";
import PagesTab from "@/components/project/PagesTab";
import ClientLinksTab from "@/components/client/LinksTab";
import ClientBusinessTab from "@/components/client/BusinessTab";
import ClientPortalTab from "@/components/client/ClientPortalTab";
import DocumentsTrackerTab from "@/components/client/DocumentsTrackerTab";
import ComplianceTrackerTab from "@/components/client/ComplianceTrackerTab";

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [pages, setPages] = useState([]);
  const [editOpen, setEditOpen] = useState(false);

  const isAdminOrManager = ["Admin", "Manager"].includes(user.role);
  // Client Portal module: Admin + Manager + Executive (per v3.2 spec)
  const canManageClientPortal = ["Admin", "Manager", "Executive"].includes(user.role);
  const isAdmin = user.role === "Admin";
  // Deep-link support: `?tab=business` opens that tab directly (used by Knowledge results).
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") || "overview";
  const setTab = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "overview") next.delete("tab"); else next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  const loadAll = async () => {
    try {
      const [c, p, u, pg] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/projects`, { params: { client_id: id, view: "all" } }),
        api.get(`/users`).catch(() => ({ data: [] })),
        api.get(`/clients/${id}/pages`).catch(() => ({ data: [] })),
      ]);
      setClient(c.data);
      setProjects(p.data || []);
      setUsers(u.data || []);
      setPages(pg.data || []);
    } catch {
      toast.error("Failed to load client");
    }
  };

  useEffect(() => { loadAll(); }, [id]); // eslint-disable-line

  if (!client) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-white" data-testid="client-detail-page">
      <div className="border-b border-slate-200 bg-white">
        <div className="px-4 sm:px-8 py-6">
          <Link to="/clients" className="text-xs text-slate-500 inline-flex items-center gap-1 hover:text-[#0a2540]" data-testid="back-to-clients">
            <ArrowLeft size={12} /> Clients
          </Link>
          <div className="mt-3 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="flex items-end gap-3 sm:gap-4 min-w-0 flex-1">
              <ClientLogo fileId={client.logo_file_id} name={client.name} size={56} />
              <div className="min-w-0">
                <div className="gc-label mb-1">Client</div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate" style={{ fontFamily: "Outfit" }} data-testid="client-name">
                  {client.name}
                </h1>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] sm:text-[11px] font-mono-data text-slate-600">
                  {client.cin && <span>CIN: <span className="text-[#0a2540]">{client.cin}</span></span>}
                  {client.pan && <span>PAN: <span className="text-[#0a2540]">{client.pan}</span></span>}
                  {client.gstin && <span>GSTIN: <span className="text-[#0a2540]">{client.gstin}</span></span>}
                  {client.tan && <span>TAN: <span className="text-[#0a2540]">{client.tan}</span></span>}
                </div>
              </div>
            </div>
            {isAdminOrManager && (
              <div className="flex items-center gap-2 shrink-0">
                <LogoUploader client={client} onUploaded={loadAll} />
                <Button variant="outline" className="rounded-sm" onClick={() => setEditOpen(true)} data-testid="btn-edit-client">
                  <Pencil size={14} className="mr-1.5" /> <span className="hidden sm:inline">Edit Details</span><span className="sm:hidden">Edit</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Tabs value={tabFromUrl} onValueChange={setTab} className="px-4 sm:px-8 py-6">
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none w-full justify-start h-auto p-0 overflow-x-auto flex-nowrap">
          <TabsTrigger value="overview" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="business" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-business">Business</TabsTrigger>
          <TabsTrigger value="projects" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-projects">Projects ({projects.filter((p) => p.status !== "Completed").length})</TabsTrigger>
          <TabsTrigger value="pages" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-pages">Pages ({pages.length})</TabsTrigger>
          <TabsTrigger value="links" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-links">Links ({(client.links || []).length})</TabsTrigger>
          <TabsTrigger value="compliance" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-compliance">Databases</TabsTrigger>
          <TabsTrigger value="documents" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="compliance-tracker" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-compliance-tracker">Compliance</TabsTrigger>
          <TabsTrigger value="portal" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-portal">Client Portal</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {[
              ["Registered Address", client.registered_address, false],
              ["Date of Incorporation", fmtDate(client.date_of_incorporation, ""), true],
              ["CIN", client.cin, true],
              ["PAN", client.pan, true],
              ["GSTIN", client.gstin, true],
              ["TAN", client.tan, true],
              ["Udhyam", client.udhyam, true],
              ["Contact Email", client.contact_email, false],
              ["Contact Phone", client.contact_phone, true],
              ...Object.entries(client.custom_fields || {}).map(([k, v]) => [k, v, false]),
            ].map(([label, val, mono]) => (
              <div key={label} className="bg-white px-4 py-3">
                <div className="gc-label text-[10px]">{label}</div>
                <div className={`mt-1 text-sm ${mono ? "font-mono-data text-[#0a2540]" : "text-[#0f172a]"}`}>
                  {val || <span className="text-slate-300">—</span>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Projects */}
        <TabsContent value="projects" className="pt-6">
          <ClientProjectsTab
            clientId={id}
            projects={projects}
            users={users}
            canEdit={isAdminOrManager}
            onChanged={loadAll}
          />
        </TabsContent>

        {/* Business — operations playbook (revenue/expense heads, vendors, etc.) */}
        <TabsContent value="business" className="pt-6">
          <ClientBusinessTab clientId={id} canEdit={isAdminOrManager} />
        </TabsContent>

        {/* Pages — company-level notes, drafts, research */}
        <TabsContent value="pages" className="pt-6">
          <PagesTab
            scope="client"
            parentId={id}
            pages={pages}
            isAdmin={isAdmin}
            onChanged={loadAll}
          />
        </TabsContent>

        {/* Links — Drive folders, portal logins, etc. */}
        <TabsContent value="links" className="pt-6">
          <ClientLinksTab
            clientId={id}
            links={client.links || []}
            canEdit={isAdminOrManager}
            onChanged={loadAll}
          />
        </TabsContent>

        {/* Compliance DBs */}
        <TabsContent value="compliance" className="pt-6">
          <DatabasesPanel clientId={id} />
        </TabsContent>

        {/* Documents Availability Tracker */}
        <TabsContent value="documents" className="pt-6">
          <DocumentsTrackerTab clientId={id} />
        </TabsContent>

        {/* Compliance stage-workflow Tracker */}
        <TabsContent value="compliance-tracker" className="pt-6">
          <ComplianceTrackerTab clientId={id} />
        </TabsContent>

        {/* Client Portal — manage external client users + data uploads + RFIs */}
        <TabsContent value="portal" className="pt-6">
          <ClientPortalTab clientId={id} canManage={canManageClientPortal} isAdmin={user.role === "Admin"} />
        </TabsContent>
      </Tabs>

      <EditClientDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        client={client}
        onSaved={loadAll}
      />
    </div>
  );
}
